import { Check, Layers, Box, SlidersHorizontal, Thermometer } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import FAQ from "@/components/FAQ";
import { Eyebrow, GradientText, PrimaryButton } from "@/components/Brand";
import { Reveal } from "@/lib/motion";

const WHAT_CARDS = [
  { icon: Layers, title: "Multi Room", text: "Condition several rooms." },
  { icon: Box, title: "One Outdoor Unit", text: "Reduce the number of outdoor condensers." },
  { icon: SlidersHorizontal, title: "Individual Control", text: "Control compatible indoor heads independently." },
  { icon: Thermometer, title: "Heating + Cooling", text: "Reverse-cycle comfort throughout the year." },
];

const BENEFITS = [
  "Fewer outdoor units",
  "Independent room control",
  "Multiple compatible indoor unit styles",
  "Flexible for many houses and apartments",
  "Heating and cooling",
  "Condition only the occupied areas",
  "Cleaner exterior appearance",
];

const COMPARISON = [
  { feature: "Number of rooms", split: "1", multi: "Multiple", ducted: "Whole home / multiple" },
  { feature: "Outdoor units", split: "Generally 1 per split", multi: "One shared compatible condenser", ducted: "Usually one outdoor condenser" },
  { feature: "Indoor appearance", split: "Wall-mounted indoor unit", multi: "Multiple indoor units", ducted: "Discreet outlets" },
  { feature: "Independent control", split: "Yes", multi: "Yes", ducted: "Via zoning where configured" },
  { feature: "Ductwork required", split: "No", multi: "No", ducted: "Yes" },
  { feature: "Best suited for", split: "Individual room", multi: "Multiple separate rooms", ducted: "Whole-home comfort" },
];

const FAQS = [
  { q: "How many indoor units can connect to one outdoor unit?", a: "The number varies according to the manufacturer and model. We can recommend a compatible combination based on the property." },
  { q: "Can each room have a different temperature?", a: "Compatible multi-head systems generally allow individual indoor units to be controlled separately." },
  { q: "Is multi-head better than ducted?", a: "Neither option is universally better. The right system depends on property layout, ceiling space, rooms being conditioned, appearance, budget and usage patterns." },
  { q: "Can different types of indoor units be installed?", a: "Some manufacturers allow compatible wall-mounted, bulkhead, cassette or other indoor unit combinations." },
  { q: "Can multi-head systems qualify for VEU incentives?", a: "Selected approved systems may qualify depending on existing equipment, system configuration and current Victorian Energy Upgrades rules." },
];

export default function MultiHead() {
  return (
    <>
      <SEO
        title="Multi Head Split Systems Melbourne | Airconditioning Victoria"
        description="Heat and cool multiple rooms using multiple indoor air conditioning units connected to one compatible outdoor condenser."
        path="/multi-head-split-systems"
      />
      <PageHero
        eyebrow="Multi-Head Air Conditioning"
        titleLines={["Multi-room comfort.", <GradientText key="g">One outdoor system.</GradientText>]}
        intro="A multi-head split system connects several compatible indoor air conditioning units to a single outdoor condenser. It can be a practical option for properties requiring independent temperature control across several rooms without installing multiple outdoor units."
        ctaText="Get a Multi-Head Quote"
        image="/assets/multihead-hero.webp"
        imageAlt="Two-storey Victorian home with multi-head split system indoor units connected to one Daikin outdoor condenser"
      />

      {/* WHAT IS */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="multi-what-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-3xl">
            <Eyebrow>The System</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              What is a multi-head split system?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue">
              Instead of installing a separate outdoor condenser for every indoor air conditioner, a
              multi-head configuration allows multiple compatible indoor units to connect to one
              outdoor system. Each indoor unit can generally be controlled independently.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHAT_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-[18px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ice">
                    <c.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <h3 className="mt-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedblue">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white py-24 md:py-32" data-testid="multi-benefits-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Benefits</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Why choose <GradientText>multi-head?</GradientText>
            </h2>
          </Reveal>
          <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b} delay={i * 0.05}>
                <li className="flex h-full items-start gap-3 rounded-[16px] border border-cardline bg-white p-5 text-sm font-medium text-ink transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-cyan">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-brand">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  {b}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="bg-cloud py-24 md:py-32" data-testid="multi-comparison-section">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow className="justify-center">Compare Systems</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Split, multi-head or ducted?
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 hidden overflow-hidden rounded-[20px] border border-cardline bg-white shadow-[0_8px_32px_rgba(5,59,117,0.05)] md:block">
            <table className="w-full text-left text-sm" data-testid="comparison-table">
              <thead>
                <tr className="bg-navy text-white">
                  <th className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.15em]">Feature</th>
                  <th className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.15em]">Standard Split</th>
                  <th className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-brand-cyan">Multi-Head</th>
                  <th className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.15em]">Ducted</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.feature} className={i % 2 === 0 ? "bg-white" : "bg-offwhite"}>
                    <th className="px-6 py-4 font-display text-xs font-bold uppercase tracking-wider text-navy">{row.feature}</th>
                    <td className="px-6 py-4 text-mutedblue">{row.split}</td>
                    <td className="px-6 py-4 font-medium text-navy">{row.multi}</td>
                    <td className="px-6 py-4 text-mutedblue">{row.ducted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>

          <div className="mt-12 space-y-4 md:hidden" data-testid="comparison-cards">
            {COMPARISON.map((row) => (
              <Reveal key={row.feature}>
                <div className="rounded-[16px] border border-cardline bg-white p-5">
                  <h3 className="font-display text-xs font-bold uppercase tracking-[0.15em] text-navy">{row.feature}</h3>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between gap-4"><dt className="text-mutedblue">Standard Split</dt><dd className="text-right font-medium text-ink">{row.split}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-mutedblue">Multi-Head</dt><dd className="text-right font-semibold text-brand-mid">{row.multi}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-mutedblue">Ducted</dt><dd className="text-right font-medium text-ink">{row.ducted}</dd></div>
                  </dl>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 md:py-32" data-testid="multi-faq-section">
        <div className="mx-auto max-w-4xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Common Questions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Multi-head FAQs.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <FAQ items={FAQS} testidPrefix="multi-faq" />
          </Reveal>
          <Reveal delay={0.15} className="mt-10 text-center">
            <PrimaryButton to="/contact" testid="multi-faq-cta">Get a Multi-Head Quote</PrimaryButton>
          </Reveal>
        </div>
      </section>

      <FinalCTA
        heading="Multiple rooms. One outdoor unit."
        text="Talk to Airconditioning Victoria about whether a multi-head split system suits your property."
      />
    </>
  );
}
